export type CommercialPackageId = 'base' | 'complete' | 'management' | 'enterprise';
export type CommercialBillingCycle = 'monthly' | 'annual';

export type CommercialAddonId =
  | 'warehouse_pro'
  | 'food_cost'
  | 'menu_qr'
  | 'transport_documents'
  | 'photo_lots'
  | 'sdi'
  | 'catering';

export interface CommercialPackage {
  id: CommercialPackageId;
  name: string;
  monthlyPrice: number | null;
  priceVisibility: 'public' | 'quote';
  recommended?: boolean;
  setupFee: number | null;
  summary: string;
}

export interface CommercialFeature {
  id: string;
  label: string;
  availability: Readonly<Record<CommercialPackageId, boolean>>;
}

export interface CommercialAddon {
  id: CommercialAddonId;
  name: string;
  price: number;
  note: string;
  status: 'vendibile' | 'assistito' | 'pilota';
}

export const ANNUAL_DISCOUNT_RATE = 0.10;
export const CONSULTANT_BASE_RATE = 0.20;
export const CONSULTANT_UPSELL_RATE = 0.40;
export const CONSULTANT_BASE_LIMIT = 130;

export const COMMERCIAL_PACKAGES: readonly CommercialPackage[] = [
  {
    id: 'base',
    name: 'Base',
    monthlyPrice: 80,
    priceVisibility: 'public',
    setupFee: 150,
    summary: 'Operatività essenziale con pulizie e temperature',
  },
  {
    id: 'complete',
    name: 'HACCP Completo',
    monthlyPrice: 130,
    priceVisibility: 'public',
    recommended: true,
    setupFee: 250,
    summary: 'La proposta centrale per una gestione HACCP completa',
  },
  {
    id: 'management',
    name: 'Gestione',
    monthlyPrice: 180,
    priceVisibility: 'public',
    setupFee: 250,
    summary: 'Controllo HACCP, logistico ed economico',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: null,
    priceVisibility: 'quote',
    setupFee: null,
    summary: 'Gestione totale e flussi costruiti sull’organizzazione',
  },
] as const;

export const COMMERCIAL_FEATURES: readonly CommercialFeature[] = [
  {
    id: 'labels_traceability',
    label: 'Etichette, carico DDT e tracciabilità',
    availability: { base: true, complete: true, management: true, enterprise: true },
  },
  {
    id: 'cleaning_temperatures',
    label: 'Pulizie, temperature e registri relativi',
    availability: { base: true, complete: true, management: true, enterprise: true },
  },
  {
    id: 'all_registers',
    label: 'Tutti i registri HACCP MOD 01–11',
    availability: { base: false, complete: true, management: true, enterprise: true },
  },
  {
    id: 'manual_staff',
    label: 'Manuale, personale e attestati',
    availability: { base: false, complete: true, management: true, enterprise: true },
  },
  {
    id: 'warehouse_pro',
    label: 'Magazzino Pro, FEFO e scadenze',
    availability: { base: false, complete: true, management: true, enterprise: true },
  },
  {
    id: 'food_cost',
    label: 'Food Cost, margini e report',
    availability: { base: false, complete: false, management: true, enterprise: true },
  },
  {
    id: 'outbound_ddt',
    label: 'DDT in uscita',
    availability: { base: false, complete: false, management: true, enterprise: true },
  },
  {
    id: 'sdi',
    label: 'SDI e-fatture',
    availability: { base: false, complete: false, management: false, enterprise: true },
  },
  {
    id: 'custom_features',
    label: 'Nuove funzioni e flussi personalizzati',
    availability: { base: false, complete: false, management: false, enterprise: true },
  },
  {
    id: 'dedicated_consulting',
    label: 'Consulenza dedicata',
    availability: { base: false, complete: false, management: false, enterprise: true },
  },
] as const;

export const COMMERCIAL_ADDONS: readonly CommercialAddon[] = [
  { id: 'warehouse_pro', name: 'Magazzino Pro', price: 30, note: 'Quantità, FEFO, scadenze, scarichi e rettifiche', status: 'vendibile' },
  { id: 'food_cost', name: 'Food Cost', price: 40, note: 'Da 40 €/mese: setup Food Cost, mantenimento, esportazioni e personalizzazioni concordate', status: 'assistito' },
  { id: 'menu_qr', name: 'Menù digitale', price: 20, note: 'QR pubblico, allergeni, prezzi, foto e lingue; setup iniziale del menu incluso nel canone', status: 'vendibile' },
  { id: 'transport_documents', name: 'DDT in uscita', price: 20, note: 'Vendite e trasferimenti interni con lotti', status: 'pilota' },
  { id: 'photo_lots', name: 'Foto lotti AI', price: 15, note: 'Modulo indipendente: foto e lettura dei lotti anche senza Magazzino Pro', status: 'pilota' },
  { id: 'sdi', name: 'SDI e-fatture', price: 15, note: 'Provider e relativo costo esclusi', status: 'pilota' },
  { id: 'catering', name: 'Catering / Eventi', price: 60, note: 'Eventi, approvvigionamenti e preventivi', status: 'pilota' },
] as const;

export const PACKAGE_INCLUDED_ADDONS: Readonly<Record<CommercialPackageId, readonly CommercialAddonId[]>> = {
  base: [],
  complete: ['warehouse_pro'],
  management: ['warehouse_pro', 'food_cost', 'transport_documents'],
  enterprise: ['warehouse_pro', 'food_cost', 'transport_documents', 'sdi'],
};

export interface CommercialQuoteInput {
  packageId: CommercialPackageId;
  billingCycle: CommercialBillingCycle;
  devices: number;
  selectedAddonIds: readonly CommercialAddonId[];
  extraDocumentPacks: number;
  connectedProbes: number;
}

export interface CommercialQuote {
  packageDefinition: CommercialPackage;
  deviceSurcharge: number | null;
  requiresDeviceQuote: boolean;
  effectiveAddonIds: CommercialAddonId[];
  billableAddonIds: CommercialAddonId[];
  addonTotal: number;
  documentPackTotal: number;
  probeTotal: number;
  monthlyTotal: number | null;
  annualFullPrice: number | null;
  annualUpfrontTotal: number | null;
  annualSavings: number | null;
  billingTotal: number | null;
}

export interface ConsultantScenario {
  clients: number;
  commissionRate: number;
  monthlyRevenueEquivalent: number;
  monthlyCommissionEquivalent: number;
  annualRevenue: number;
  annualCommission: number;
}

export interface ConsultantCommission {
  monthlyContractValue: number;
  baseValue: number;
  upsellValue: number;
  baseCommission: number;
  upsellCommission: number;
  monthlyCommission: number;
  effectiveRate: number;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function nonNegativeInteger(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

export function annualFullPriceFromMonthly(monthlyPrice: number): number {
  return monthlyPrice * 12;
}

export function annualUpfrontPriceFromMonthly(monthlyPrice: number): number {
  return annualFullPriceFromMonthly(monthlyPrice) * (1 - ANNUAL_DISCOUNT_RATE);
}

export function deviceSurcharge(devices: number): number | null {
  const count = Math.max(1, nonNegativeInteger(devices));
  if (count <= 3) return 0;
  if (count <= 5) return 10;
  if (count <= 10) return 25;
  if (count <= 20) return 50;
  return null;
}

export function calculateConsultantCommission(monthlyContractValue: number): ConsultantCommission {
  const value = Number.isFinite(monthlyContractValue) ? roundMoney(Math.max(0, monthlyContractValue)) : 0;
  const baseValue = Math.min(value, CONSULTANT_BASE_LIMIT);
  const upsellValue = roundMoney(Math.max(0, value - CONSULTANT_BASE_LIMIT));
  const baseCommission = roundMoney(baseValue * CONSULTANT_BASE_RATE);
  const upsellCommission = roundMoney(upsellValue * CONSULTANT_UPSELL_RATE);
  const monthlyCommission = roundMoney(baseCommission + upsellCommission);

  return {
    monthlyContractValue: value,
    baseValue,
    upsellValue,
    baseCommission,
    upsellCommission,
    monthlyCommission,
    effectiveRate: value === 0 ? 0 : monthlyCommission / value,
  };
}

export function parseConsultantMonthlyPrice(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const price = Number(normalized);
  return Number.isFinite(price) && price > 0 && price <= 100000 ? price : null;
}

export function parseConsultantClientCount(value: string): number | null {
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) return null;
  const clients = Number(normalized);
  return Number.isSafeInteger(clients) && clients >= 1 && clients <= 100000 ? clients : null;
}

export function calculateConsultantScenario(
  monthlyContractValue: number,
  clients: number,
  billingCycle: CommercialBillingCycle,
): ConsultantScenario {
  const normalizedClients = nonNegativeInteger(clients);
  const commission = calculateConsultantCommission(monthlyContractValue);
  // Le fasce si applicano al canone concordato; il numero clienti è soltanto un moltiplicatore.
  const annualMultiplier = 12 * (billingCycle === 'annual' ? 1 - ANNUAL_DISCOUNT_RATE : 1);
  const annualRevenue = roundMoney(roundMoney(commission.monthlyContractValue * annualMultiplier) * normalizedClients);
  const annualCommission = roundMoney(roundMoney(commission.monthlyCommission * annualMultiplier) * normalizedClients);

  return {
    clients: normalizedClients,
    commissionRate: commission.effectiveRate,
    monthlyRevenueEquivalent: roundMoney(annualRevenue / 12),
    monthlyCommissionEquivalent: roundMoney(annualCommission / 12),
    annualRevenue,
    annualCommission,
  };
}

export function calculateCommercialQuote(input: CommercialQuoteInput): CommercialQuote {
  const packageDefinition = COMMERCIAL_PACKAGES.find((item) => item.id === input.packageId)
    ?? COMMERCIAL_PACKAGES[0];
  const effectiveAddonIds = new Set<CommercialAddonId>(input.selectedAddonIds);
  PACKAGE_INCLUDED_ADDONS[packageDefinition.id].forEach((addonId) => effectiveAddonIds.add(addonId));

  const includedAddonIds = new Set<CommercialAddonId>(PACKAGE_INCLUDED_ADDONS[packageDefinition.id]);
  const billableAddonIds = [...effectiveAddonIds].filter((addonId) => !includedAddonIds.has(addonId));
  const addonTotal = billableAddonIds.reduce((total, addonId) => (
    total + (COMMERCIAL_ADDONS.find((item) => item.id === addonId)?.price ?? 0)
  ), 0);
  const documentPackTotal = nonNegativeInteger(input.extraDocumentPacks) * 10;
  const probeTotal = nonNegativeInteger(input.connectedProbes) * 10;
  const devicesPrice = deviceSurcharge(input.devices);
  const monthlyTotal = devicesPrice === null || packageDefinition.monthlyPrice === null
    ? null
    : packageDefinition.monthlyPrice + devicesPrice + addonTotal + documentPackTotal + probeTotal;
  const annualFullPrice = monthlyTotal === null ? null : annualFullPriceFromMonthly(monthlyTotal);
  const annualUpfrontTotal = monthlyTotal === null ? null : annualUpfrontPriceFromMonthly(monthlyTotal);

  return {
    packageDefinition,
    deviceSurcharge: devicesPrice,
    requiresDeviceQuote: devicesPrice === null,
    effectiveAddonIds: [...effectiveAddonIds],
    billableAddonIds,
    addonTotal,
    documentPackTotal,
    probeTotal,
    monthlyTotal,
    annualFullPrice,
    annualUpfrontTotal,
    annualSavings: annualFullPrice === null || annualUpfrontTotal === null
      ? null
      : annualFullPrice - annualUpfrontTotal,
    billingTotal: input.billingCycle === 'annual' ? annualUpfrontTotal : monthlyTotal,
  };
}

export function formatCommercialQuoteSummary(input: CommercialQuoteInput): string {
  const quote = calculateCommercialQuote(input);
  const enterprise = quote.packageDefinition.priceVisibility === 'quote';
  const parts = [
    enterprise
      ? `${quote.packageDefinition.name}: offerta personalizzata`
      : `${quote.packageDefinition.name}: ${quote.packageDefinition.monthlyPrice} €/mese`,
    `${Math.max(1, nonNegativeInteger(input.devices))} dispositivi`,
  ];
  if (quote.billableAddonIds.length > 0) {
    const names = quote.billableAddonIds.map((id) => COMMERCIAL_ADDONS.find((item) => item.id === id)?.name ?? id);
    parts.push(`moduli: ${names.join(', ')}`);
  }
  if (input.extraDocumentPacks > 0) parts.push(`+${nonNegativeInteger(input.extraDocumentPacks) * 100} analisi documenti`);
  if (input.connectedProbes > 0) parts.push(`${nonNegativeInteger(input.connectedProbes)} sonde connesse`);
  if (!enterprise) {
    if (
      quote.monthlyTotal === null
      || quote.annualFullPrice === null
      || quote.annualUpfrontTotal === null
      || quote.annualSavings === null
    ) {
      parts.push('totale dispositivi su preventivo');
    } else {
      parts.push(`canone risultante ${quote.monthlyTotal} €/mese + IVA`);
      parts.push(`costo annuo a prezzo pieno ${quote.annualFullPrice} € + IVA`);
      parts.push(`pagamento annuale anticipato ${quote.annualUpfrontTotal} € una tantum + IVA (sconto 10%, risparmio ${quote.annualSavings} €)`);
      parts.push(input.billingCycle === 'annual' ? 'modalità scelta: annuale anticipato' : 'modalità scelta: mensile');
    }
    parts.push(quote.packageDefinition.setupFee === null
      ? 'setup da definire'
      : `setup da ${quote.packageDefinition.setupFee} € + IVA`);
  } else {
    parts.push('canone e setup da definire in proposta');
  }
  return parts.join(' · ');
}
